import { z } from 'zod';

export const roleSchema = z.object({
  id: z.coerce.number(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  assignedUserCount: z.coerce.number(),
  heldPermissionCount: z.coerce.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const roleListSchema = z.array(roleSchema);

export type Role = z.infer<typeof roleSchema>;

export const ROLE_NAME_MAX_LENGTH = 100;
export const ROLE_DESCRIPTION_MAX_LENGTH = 500;
export const ROLE_CODE_MAX_LENGTH = 60;
export const ROLE_CODE_PATTERN = /^[A-Z0-9_]+$/;

export const PROTECTED_ROLE_CODE = 'SUPER_ADMINISTRATOR';

export function isProtectedRole(role: { code: string }): boolean {
  return role.code === PROTECTED_ROLE_CODE;
}

export const roleFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .max(ROLE_CODE_MAX_LENGTH, `Code must be ${ROLE_CODE_MAX_LENGTH} characters or fewer`)
    .regex(ROLE_CODE_PATTERN, 'Code may hold upper-case letters, digits and underscores only'),
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(ROLE_NAME_MAX_LENGTH, `Name must be ${ROLE_NAME_MAX_LENGTH} characters or fewer`),
  description: z
    .string()
    .trim()
    .max(
      ROLE_DESCRIPTION_MAX_LENGTH,
      `Details must be ${ROLE_DESCRIPTION_MAX_LENGTH} characters or fewer`,
    ),
  isActive: z.boolean(),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;

export interface CreateRolePayload {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
}

export type UpdateRolePayload = Partial<Omit<CreateRolePayload, 'code'>>;
